// useMessageStatusSocket.ts
import { useState, useRef, useEffect, useCallback } from 'react';
import { useRecoilCallback } from 'recoil';
import { messageListAtom } from '../../Services/_states/index.js';

interface WebSocketOptions {
  url?: string;
  autoReconnect?: boolean;
  reconnectInterval?: number;
}

export function useMessageStatusSocket({
  url,
  autoReconnect = true,
  reconnectInterval = 3000,
}: WebSocketOptions) {
  const [status, setStatus] = useState<'Disconnected' | 'Connecting' | 'Connected' | 'Error'>(
    'Disconnected'
  );
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyStatusToAtom = useRecoilCallback(
    ({ set }) =>
      (data: any) => {
        set(messageListAtom, (prev: any) => {
          if (!prev?.messages?.length) return prev;

          const needsUpdate = prev.messages.some(
            (m: any) => m.w_id === data.w_id || m.msg_id === data.w_id
          );
          if (!needsUpdate) return prev;

          const statusOrder = ['sent', 'delivered', 'read', 'failed'];

          return {
            ...prev,
            messages: prev.messages.map((m: any) => {
              const isMatch = m.w_id === data.w_id || m.msg_id === data.w_id;
              if (!isMatch) return m;

              const existingRank = statusOrder.indexOf(m.status ?? 'sent');
              const incomingRank = statusOrder.indexOf(data.status);
              const shouldUpgrade = data.status === 'failed' || incomingRank > existingRank;

              return shouldUpgrade
                ? { ...m, status: data.status, status_timestamp: data.timestamp }
                : m;
            }),
          };
        });
      },
    []
  );

  const connect = useCallback(() => {
    if (!url) return;

    try {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      setStatus('Connecting');
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('Connected');
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (
            payload?.type === 'status_notification' &&
            payload?.notification_type === 'message_status_update' &&
            payload?.data?.w_id
          ) {
            applyStatusToAtom(payload.data);
          }
        } catch (e) {
          console.error('Invalid WS message:', event.data);
        }
      };

      ws.onclose = (event) => {
        setStatus('Disconnected');
        wsRef.current = null;
        if (autoReconnect && event.code !== 1000) {
          reconnectRef.current = setTimeout(connect, reconnectInterval);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ MessageStatus WS Error:', error);
        setStatus('Error');
      };
    } catch (error) {
      console.error('❌ MessageStatus WS Connection Error:', error);
      setStatus('Error');
    }
  }, [url, autoReconnect, reconnectInterval, applyStatusToAtom]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
    }
    setStatus('Disconnected');
  }, []);

  useEffect(() => {
    if (url) connect();
    return () => disconnect();
  }, [url, connect, disconnect]);

  return { status, disconnect, reconnect: connect };
}